/**
 * CLIKKER Product Workflows
 * Asset: clikker-product-workflows.js
 * Version: 1.2.4 — refreshed default product cover images + media metadata
 *
 * PURPOSE
 * --------------------------------------------------------------------------
 * Registers the six launch workflows used by Clikker creators.
 * Compatible with clikker-product-engine.js v1.2.2+.
 *
 * All product/buyer/fulfilment field keys use snake_case so workflow hooks,
 * visibility rules and Firestore product payloads use one canonical schema.
 * Existing order completionFields remain camelCase for backward compatibility;
 * the Product Engine exposes canonicalCompletionFields alongside them.
 *
 * Workflows:
 *
 * 1. Personal Reply
 * 2. Video Message
 * 3. One-to-One Call
 * 4. Digital Product
 * 5. Custom Service
 * 6. Social Promotion
 *
 * LOAD ORDER
 * --------------------------------------------------------------------------
 * 1. clikker-product-engine.js
 * 2. clikker-product-workflows.js
 * 3. clikker-profile.js
 *
 * IMPORTANT
 * --------------------------------------------------------------------------
 * - These are shared workflow blueprints.
 * - Every creator can create multiple products under the same workflow.
 * - Creator-specific title, price, description, files, settings and buyer
 *   questions are saved in each product document.
 */
(function (window) {
  "use strict";

  var VERSION = "1.2.4";
  var Engine = window.ClikkerProductEngine;

  /*
   * DEFAULT PRODUCT COVER IMAGES
   * ------------------------------------------------------------------------
   * One canonical fallback image per launch workflow.
   *
   * Display priority elsewhere in Clikker should be:
   * 1. Creator-uploaded product cover
   * 2. Workflow defaultCoverImage
   * 3. Workflow icon
   *
   * These Shopify CDN assets are intentionally defined once here so creator
   * dashboard and public storefront can use the same branded fallback.
   */
  var DEFAULT_COVER_IMAGES = Object.freeze({
    creator_reply:
      "https://cdn.shopify.com/s/files/1/0928/6136/6587/files/Personal_Reply.png?v=1786800269",
    video_message:
      "https://cdn.shopify.com/s/files/1/0928/6136/6587/files/Video_Message.png?v=1786800269",
    scheduled_call:
      "https://cdn.shopify.com/s/files/1/0928/6136/6587/files/One_to_One_Call.png?v=1786800269",
    digital_download:
      "https://cdn.shopify.com/s/files/1/0928/6136/6587/files/Digital_Product.png?v=1786800269",
    custom_service:
      "https://cdn.shopify.com/s/files/1/0928/6136/6587/files/Custom_Service.png?v=1786800269",
    social_promotion:
      "https://cdn.shopify.com/s/files/1/0928/6136/6587/files/Social_Promotion.png?v=1786800269"
  });

  function defaultCoverImage(type) {
    return DEFAULT_COVER_IMAGES[Engine.normalizeType(type)] || "";
  }

  if (
    window.ClikkerProductWorkflows &&
    window.ClikkerProductWorkflows.loaded === true
  ) {
    console.warn(
      "[Clikker Product Workflows] Already loaded",
      window.ClikkerProductWorkflows.version
    );
    return;
  }

  if (!Engine) {
    throw new Error(
      "ClikkerProductEngine must load before clikker-product-workflows.js."
    );
  }

  if (
    typeof Engine.registerWorkflows !== "function" ||
    typeof Engine.normalizeField !== "function" ||
    typeof Engine.normalizeType !== "function" ||
    typeof Engine.canonicalOrderField !== "function" ||
    Engine.supportsPublishRequired !== true ||
    Engine.supportsRichWorkflowDefaults !== true
  ) {
    throw new Error(
      "A compatible Clikker Product Engine v1.2.2 or newer is required."
    );
  }

  function textField(key, label, placeholder, required, extra) {
    return Object.assign(
      {
        key: key,
        label: label,
        type: "text",
        placeholder: placeholder || "",
        required: required === true
      },
      extra || {}
    );
  }

  function textareaField(key, label, placeholder, required, extra) {
    return Object.assign(
      {
        key: key,
        label: label,
        type: "textarea",
        placeholder: placeholder || "",
        required: required === true,
        rows: 4
      },
      extra || {}
    );
  }

  function numberField(key, label, defaultValue, required, extra) {
    return Object.assign(
      {
        key: key,
        label: label,
        type: "number",
        defaultValue: defaultValue,
        required: required === true
      },
      extra || {}
    );
  }

  function selectField(
    key,
    label,
    options,
    defaultValue,
    required,
    extra
  ) {
    return Object.assign(
      {
        key: key,
        label: label,
        type: "select",
        options: options,
        defaultValue: defaultValue,
        required: required === true
      },
      extra || {}
    );
  }

  function toggleField(key, label, defaultValue, helpText, extra) {
    return Object.assign(
      {
        key: key,
        label: label,
        type: "toggle",
        defaultValue: defaultValue === true,
        required: false,
        helpText: helpText || ""
      },
      extra || {}
    );
  }

  function fileField(key, label, accept, required, extra) {
    return Object.assign(
      {
        key: key,
        label: label,
        type: "file",
        accept: accept || "",
        required: required === true
      },
      extra || {}
    );
  }

  function buyerField(
    key,
    label,
    type,
    required,
    options,
    extra
  ) {
    return Object.assign(
      {
        key: key,
        label: label,
        type: type || "text",
        required: required === true,
        options: options || [],
        system: false,
        locked: false
      },
      extra || {}
    );
  }


  function optionalCreator(extra) {
    return Object.assign(
      {
        required: false,
        publishRequired: false
      },
      extra || {}
    );
  }

  function recommendedCreator(extra) {
    return Object.assign(
      {
        required: false,
        publishRequired: false,
        recommended: true
      },
      extra || {}
    );
  }

  function defaultTerms(overrides) {
    return Object.assign(
      {
        refund:
          "Orders are prepared specifically for the buyer. Refunds are available only when the creator cannot fulfil the order.",
        cancellation:
          "If you need to cancel or change your request, contact the creator as early as possible.",
        rescheduling:
          "Where scheduling applies, one reasonable reschedule may be requested subject to availability.",
        revision:
          "Reasonable corrections for missed approved instructions may be considered by the creator.",
        usage:
          "The purchase is for the buyer's personal use unless commercial or promotional rights are explicitly included."
      },
      overrides || {}
    );
  }

  var workflows = [

    /* ======================================================================
       1. PERSONAL REPLY
       ====================================================================== */
    {
      type: "creator_reply",

      meta: {
        icon: "💬",
        label: "Personal Reply",
        category: "Interaction",
        description:
          "Sell a private text, voice-note or short video response.",
        status: "active",
        sortOrder: 10,
        schemaVersion: 1,
        allowMultipleProducts: true,
        defaultCoverImage: defaultCoverImage("creator_reply"),
        defaultCoverAlt: "Personal Reply"
      },

      defaults: {
        title: "Ask Me Anything",
        shortDescription:
          "Send me a question and receive a personal reply directly from me.",
        description:
          "Send me your question, message or request and I will reply personally. Your response may be delivered by text, voice note or short video depending on the selected reply format.",
        customerInstructions:
          "Please share your question clearly and include any useful context. I will respond within the delivery time shown on this offer.",
        price: 299,
        currency: "INR",
        priceModel: "fixed",
        badge: "none",
        terms: defaultTerms(),
        status: "draft",
        visibility: "private",
        defaultCoverImage: defaultCoverImage("creator_reply")
      },

      fulfilmentFields: [
        selectField(
          "reply_format",
          "Reply format",
          [
            { label: "Text reply", value: "text" },
            { label: "Voice note", value: "voice_note" },
            { label: "Video reply", value: "video_reply" },
            { label: "Text or voice note", value: "text_or_voice" },
            { label: "Any available format", value: "any" }
          ],
          "text_or_voice",
          false,
          optionalCreator()
        ),

        numberField(
          "delivery_hours",
          "Delivery time in hours",
          48,
          false,
          recommendedCreator({
            min: 1,
            max: 720,
            helpText: "Change only if you want a different delivery time."
          })
        ),

        numberField(
          "daily_limit",
          "Maximum orders per day",
          5,
          false,
          optionalCreator({
            min: 1,
            max: 100
          })
        ),

        textField(
          "maximum_reply_length",
          "Maximum reply length",
          "Example: 3 minutes or 500 words",
          false,
          optionalCreator({
            defaultValue: "Up to 5 minutes or 700 words",
            maxLength: 100
          })
        ),

        selectField(
          "language",
          "Reply language",
          [
            { label: "English", value: "English" },
            { label: "Hindi", value: "Hindi" },
            { label: "Hindi and English", value: "Hindi + English" },
            { label: "Creator decides", value: "Creator decides" }
          ],
          "Hindi + English",
          false,
          optionalCreator()
        ),

        toggleField(
          "attachments_allowed",
          "Allow buyer attachments",
          false,
          "Buyers may upload references with their question."
        ),

        toggleField(
          "follow_up_allowed",
          "Allow one follow-up question",
          false,
          "The buyer may send one additional message after delivery."
        )
      ],

      buyerFields: [
        buyerField(
          "instagram_username",
          "Instagram username",
          "text",
          false
        ),

        buyerField(
          "question",
          "Your question or message",
          "textarea",
          true,
          [],
          {
            placeholder: "Tell the creator what you would like answered.",
            maxLength: 2000
          }
        ),

        buyerField(
          "preferred_format",
          "Preferred reply format",
          "select",
          false,
          [
            { label: "Text reply", value: "text" },
            { label: "Voice note", value: "voice_note" },
            { label: "Video reply", value: "video_reply" }
          ]
        )
      ],

      orderWorkflow: {
        type: "manual_reply",
        deliveryMode: "reply",
        supportsText: true,
        supportsFile: true,
        supportsLink: true,
        requiresSchedule: false,
        requiresApproval: false,
        completionFields: [
          "deliveryText",
          "deliveryFile",
          "deliveryLink"
        ]
      },

      validation: {
        minPrice: 49,
        maxPrice: 50000,
        maxBuyerFields: 10
      },

      hooks: {
        validateFulfilment: function (values) {
          var errors = [];

          if (
            Number(values.delivery_hours || 0) < 1
          ) {
            errors.push(
              "Delivery time must be at least one hour."
            );
          }

          return errors;
        }
      }
    },

    /* ======================================================================
       2. VIDEO MESSAGE
       ====================================================================== */
    {
      type: "video_message",

      meta: {
        icon: "🎥",
        label: "Video Message",
        category: "Interaction",
        description:
          "Record a personalised greeting, motivation or special message.",
        status: "active",
        sortOrder: 20,
        schemaVersion: 1,
        allowMultipleProducts: true,
        defaultCoverImage: defaultCoverImage("video_message"),
        defaultCoverAlt: "Personal Video Message"
      },

      defaults: {
        title: "Personal Video Message",
        shortDescription:
          "Receive a personalised video message made especially for you.",
        description:
          "Order a personal video message for a birthday, celebration, motivation, greeting or fan shout-out. Share the important details and I will create the message especially for the recipient.",
        customerInstructions:
          "Please provide the recipient name, occasion and any key points or pronunciation notes. Avoid including private or sensitive information that is not needed for the message.",
        price: 499,
        currency: "INR",
        priceModel: "fixed",
        badge: "none",
        terms: defaultTerms({
          revision:
            "A re-record may be considered when approved instructions were clearly missed. Style preferences alone do not guarantee a re-record."
        }),
        status: "draft",
        visibility: "private",
        defaultCoverImage: defaultCoverImage("video_message")
      },

      fulfilmentFields: [
        numberField(
          "delivery_hours",
          "Delivery time in hours",
          48,
          false,
          recommendedCreator({
            min: 1,
            max: 720
          })
        ),

        numberField(
          "maximum_video_minutes",
          "Maximum video duration",
          3,
          false,
          optionalCreator({
            min: 1,
            max: 30,
            helpText: "Default is 3 minutes. Change only if needed."
          })
        ),

        selectField(
          "orientation",
          "Video orientation",
          [
            { label: "Vertical", value: "vertical" },
            { label: "Horizontal", value: "horizontal" },
            { label: "Creator decides", value: "creator_decides" }
          ],
          "vertical",
          false,
          optionalCreator()
        ),

        selectField(
          "language",
          "Video language",
          [
            { label: "English", value: "English" },
            { label: "Hindi", value: "Hindi" },
            { label: "Hindi and English", value: "Hindi + English" },
            { label: "Creator decides", value: "Creator decides" }
          ],
          "Hindi + English",
          false,
          optionalCreator()
        ),

        numberField(
          "daily_limit",
          "Maximum videos per day",
          5,
          false,
          optionalCreator({
            min: 1,
            max: 50
          })
        ),

        toggleField(
          "rerecord_allowed",
          "Allow one re-record request",
          false,
          "Only for mistakes or missed approved instructions."
        ),

        toggleField(
          "commercial_usage_allowed",
          "Allow commercial usage",
          false,
          "Enable only when buyers may use the video for commercial purposes."
        )
      ],

      buyerFields: [
        buyerField(
          "recipient_name",
          "Recipient name",
          "text",
          true,
          [],
          {
            maxLength: 100
          }
        ),

        buyerField(
          "occasion",
          "Occasion",
          "select",
          true,
          [
            { label: "Birthday", value: "birthday" },
            { label: "Anniversary", value: "anniversary" },
            { label: "Congratulations", value: "congratulations" },
            { label: "Motivation", value: "motivation" },
            { label: "Festival greeting", value: "festival" },
            { label: "Fan shout-out", value: "fan_shoutout" },
            { label: "Other", value: "other" }
          ]
        ),

        buyerField(
          "message_instructions",
          "Message instructions",
          "textarea",
          true,
          [],
          {
            placeholder:
              "Share names, pronunciation, occasion and key points.",
            maxLength: 2000
          }
        ),

        buyerField(
          "things_to_avoid",
          "Things not to mention",
          "textarea",
          false,
          [],
          {
            maxLength: 1000
          }
        )
      ],

      orderWorkflow: {
        type: "video_delivery",
        deliveryMode: "file_or_link",
        supportsText: false,
        supportsFile: true,
        supportsLink: true,
        requiresSchedule: false,
        requiresApproval: false,
        completionFields: [
          "deliveryFile",
          "deliveryLink"
        ]
      },

      validation: {
        minPrice: 99,
        maxPrice: 100000,
        maxBuyerFields: 10
      }
    },

    /* ======================================================================
       3. ONE-TO-ONE CALL
       ====================================================================== */
    {
      type: "scheduled_call",

      meta: {
        icon: "📞",
        label: "One-to-One Call",
        category: "Booking",
        description:
          "Sell a private consultation, coaching or fan call.",
        status: "active",
        sortOrder: 30,
        schemaVersion: 1,
        allowMultipleProducts: true,
        defaultCoverImage: defaultCoverImage("scheduled_call"),
        defaultCoverAlt: "One-to-One Call"
      },

      defaults: {
        title: "Book a 1-to-1 Call",
        shortDescription:
          "Book a private 30-minute call for advice, consultation or conversation.",
        description:
          "Book a private one-to-one call with me. Use the session for advice, consultation, coaching, questions or a personal conversation. After booking, we will confirm the final time and meeting details.",
        customerInstructions:
          "Tell me what you would like to discuss and your preferred date/time. The final appointment is confirmed after I review availability.",
        price: 999,
        currency: "INR",
        priceModel: "fixed",
        badge: "none",
        terms: defaultTerms({
          cancellation:
            "Please request cancellation or changes at least 24 hours before the confirmed call whenever possible.",
          rescheduling:
            "One reschedule may be requested subject to my availability."
        }),
        status: "draft",
        visibility: "private",
        defaultCoverImage: defaultCoverImage("scheduled_call")
      },

      fulfilmentFields: [
        selectField(
          "duration_minutes",
          "Call duration",
          [
            { label: "15 minutes", value: "15" },
            { label: "30 minutes", value: "30" },
            { label: "45 minutes", value: "45" },
            { label: "60 minutes", value: "60" },
            { label: "90 minutes", value: "90" }
          ],
          "30",
          false,
          optionalCreator()
        ),

        selectField(
          "platform",
          "Meeting platform",
          [
            { label: "Google Meet", value: "google_meet" },
            { label: "Zoom", value: "zoom" },
            { label: "WhatsApp", value: "whatsapp" },
            { label: "Phone call", value: "phone" },
            { label: "Custom", value: "custom" }
          ],
          "google_meet",
          false,
          optionalCreator()
        ),

        textField(
          "custom_platform",
          "Custom platform",
          "Enter platform name",
          false,
          optionalCreator({
            visibleWhen: {
              field: "platform",
              equals: "custom"
            },
            maxLength: 100
          })
        ),

        textField(
          "timezone",
          "Creator timezone",
          "Asia/Kolkata",
          false,
          optionalCreator({
            defaultValue: "Asia/Kolkata",
            maxLength: 100
          })
        ),

        textareaField(
          "availability",
          "Availability",
          "Example: Monday to Saturday, 11 AM to 6 PM",
          false,
          recommendedCreator({
            defaultValue: "Monday to Saturday, 11:00 AM to 6:00 PM",
            maxLength: 500,
            helpText: "Default availability is prefilled. Change it if your schedule is different."
          })
        ),

        numberField(
          "minimum_notice_hours",
          "Minimum booking notice",
          24,
          false,
          optionalCreator({
            min: 1,
            max: 720,
            helpText: "Default: buyers should book at least 24 hours ahead."
          })
        ),

        numberField(
          "advance_booking_days",
          "Advance booking window",
          30,
          false,
          optionalCreator({
            min: 1,
            max: 365,
            helpText: "Default: buyers can request dates up to 30 days ahead."
          })
        ),

        numberField(
          "daily_limit",
          "Maximum calls per day",
          4,
          false,
          optionalCreator({
            min: 1,
            max: 30
          })
        ),

        toggleField(
          "rescheduling_allowed",
          "Allow one reschedule",
          true,
          "The buyer may request one reschedule subject to creator approval."
        ),

        toggleField(
          "cancellation_allowed",
          "Allow cancellation",
          true,
          "Cancellation rules should also be stated in the product terms."
        )
      ],

      buyerFields: [
        buyerField(
          "customer_phone",
          "Phone number",
          "tel",
          false
        ),

        buyerField(
          "call_reason",
          "What would you like to discuss?",
          "textarea",
          true,
          [],
          {
            maxLength: 2000
          }
        ),

        buyerField(
          "preferred_date",
          "Preferred date",
          "date",
          false
        ),

        buyerField(
          "preferred_slot",
          "Preferred time",
          "text",
          false,
          [],
          {
            placeholder: "Example: 4 PM to 6 PM"
          }
        ),

        buyerField(
          "alternate_date",
          "Alternate date",
          "date",
          false
        )
      ],

      orderWorkflow: {
        type: "scheduled_session",
        deliveryMode: "scheduled",
        supportsText: true,
        supportsFile: false,
        supportsLink: true,
        requiresSchedule: true,
        requiresApproval: true,
        completionFields: [
          "scheduledAt",
          "meetingPlatform",
          "meetingLink",
          "completionNote"
        ]
      },

      validation: {
        minPrice: 199,
        maxPrice: 250000,
        maxBuyerFields: 10
      },

      hooks: {
        validateFulfilment: function (values) {
          var errors = [];

          if (
            values.platform === "custom" &&
            !String(values.custom_platform || "").trim()
          ) {
            errors.push(
              "Custom platform is required when Custom is selected."
            );
          }

          return errors;
        }
      }
    },

    /* ======================================================================
       4. DIGITAL PRODUCT
       ====================================================================== */
    {
      type: "digital_download",

      meta: {
        icon: "📘",
        label: "Digital Product",
        category: "Digital",
        description:
          "Sell an e-book, guide, template, notes, audio or downloadable resource.",
        status: "active",
        sortOrder: 40,
        schemaVersion: 1,
        allowMultipleProducts: true,
        defaultCoverImage: defaultCoverImage("digital_download"),
        defaultCoverAlt: "Digital Product"
      },

      defaults: {
        title: "My Digital Resource",
        shortDescription:
          "Get instant access to my downloadable digital resource.",
        description:
          "Download my digital resource immediately after successful payment. This offer can be used for guides, e-books, templates, notes, checklists, audio, video or other downloadable content.",
        customerInstructions:
          "After payment, use the secure access provided with your order to download the resource. Please keep your purchase link private.",
        price: 499,
        currency: "INR",
        priceModel: "fixed",
        badge: "none",
        terms: defaultTerms({
          refund:
            "Because digital products are delivered electronically, completed downloads are generally non-refundable unless the file is defective or inaccessible.",
          usage:
            "For personal use only unless this product explicitly includes commercial or extended usage rights."
        }),
        status: "draft",
        visibility: "private",
        defaultCoverImage: defaultCoverImage("digital_download")
      },

      fulfilmentFields: [
        fileField(
          "digital_file",
          "Digital product file",
          ".pdf,.zip,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.mp3,.wav,.mp4,.jpg,.jpeg,.png",
          true,
          {
            helpText:
              "Upload the file buyers should receive after successful payment."
          }
        ),

        textField(
          "file_version",
          "File version",
          "1.0",
          false,
          optionalCreator({
            defaultValue: "1.0",
            maxLength: 30
          })
        ),

        textField(
          "target_audience",
          "Target audience",
          "Who is this product for?",
          false,
          optionalCreator({
            defaultValue: "Anyone looking for practical, easy-to-use digital guidance and resources.",
            maxLength: 200
          })
        ),

        textareaField(
          "included_items",
          "What is included?",
          "List the files, chapters or resources included.",
          false,
          optionalCreator({
            defaultValue: "Includes the complete downloadable resource and any files shown in this product.",
            maxLength: 2000
          })
        ),

        selectField(
          "access_duration",
          "Download access duration",
          [
            { label: "7 days", value: "7_days" },
            { label: "30 days", value: "30_days" },
            { label: "90 days", value: "90_days" },
            { label: "Lifetime", value: "lifetime" }
          ],
          "lifetime",
          false,
          optionalCreator()
        ),

        numberField(
          "download_limit",
          "Download limit",
          5,
          false,
          {
            min: 1,
            max: 100
          }
        ),

        toggleField(
          "allow_repeat_download",
          "Allow repeat downloads",
          true,
          "Buyers may download again while access remains active."
        ),

        selectField(
          "licence_type",
          "Licence type",
          [
            { label: "Personal use", value: "personal" },
            { label: "Commercial use", value: "commercial" },
            { label: "Extended licence", value: "extended" }
          ],
          "personal",
          false,
          optionalCreator()
        )
      ],

      buyerFields: [
        buyerField(
          "customer_note",
          "Optional note",
          "textarea",
          false,
          [],
          {
            maxLength: 1000
          }
        )
      ],

      orderWorkflow: {
        type: "instant_download",
        deliveryMode: "instant_file",
        supportsText: false,
        supportsFile: true,
        supportsLink: true,
        requiresSchedule: false,
        requiresApproval: false,
        completionFields: [
          "downloadURL",
          "accessExpiresAt",
          "downloadLimit"
        ]
      },

      validation: {
        minPrice: 1,
        maxPrice: 100000,
        maxBuyerFields: 6
      }
    },

    /* ======================================================================
       5. CUSTOM SERVICE
       ====================================================================== */
    {
      type: "custom_service",

      meta: {
        icon: "✨",
        label: "Custom Service",
        category: "Service",
        description:
          "Sell personalised work, advice, reviews, plans or creative services.",
        status: "active",
        sortOrder: 50,
        schemaVersion: 1,
        allowMultipleProducts: true,
        defaultCoverImage: defaultCoverImage("custom_service"),
        defaultCoverAlt: "Custom Service"
      },

      defaults: {
        title: "Custom Service",
        shortDescription:
          "Tell me what you need and receive a personalised service made for you.",
        description:
          "Book a personalised service based on your requirements. Share your goal, references and important details and I will complete the agreed work within the delivery time shown.",
        customerInstructions:
          "Describe what you need as clearly as possible. Add references or links where useful. I may contact you if an important detail needs clarification.",
        price: 999,
        currency: "INR",
        priceModel: "fixed",
        badge: "none",
        terms: defaultTerms({
          revision:
            "One reasonable revision is included unless the product description states otherwise.",
          cancellation:
            "Cancellation may not be possible after work has started on the custom request."
        }),
        status: "draft",
        visibility: "private",
        defaultCoverImage: defaultCoverImage("custom_service")
      },

      fulfilmentFields: [
        textField(
          "deliverable",
          "Main deliverable",
          "What will the buyer receive?",
          false,
          recommendedCreator({
            defaultValue: "A personalised deliverable based on the buyer's submitted requirements.",
            maxLength: 250
          })
        ),

        numberField(
          "delivery_hours",
          "Delivery time in hours",
          72,
          false,
          recommendedCreator({
            min: 1,
            max: 2160
          })
        ),

        numberField(
          "daily_limit",
          "Maximum active orders",
          5,
          false,
          optionalCreator({
            min: 1,
            max: 100
          })
        ),

        numberField(
          "revision_limit",
          "Included revisions",
          1,
          false,
          optionalCreator({
            min: 0,
            max: 10
          })
        ),

        selectField(
          "pricing_mode",
          "Pricing mode",
          [
            { label: "Fixed price", value: "fixed" },
            { label: "Starting from", value: "starting_from" },
            { label: "Custom quote", value: "custom_quote" }
          ],
          "fixed",
          false,
          optionalCreator()
        ),

        toggleField(
          "buyer_files_required",
          "Buyer files required",
          false,
          "Enable when the buyer must upload references or source files."
        ),

        textareaField(
          "scope_included",
          "What is included?",
          "Clearly explain the included work.",
          false,
          optionalCreator({
            defaultValue: "The service includes the work described in this product and one reasonable clarification/revision where applicable.",
            maxLength: 2000
          })
        ),

        textareaField(
          "scope_exclusions",
          "What is not included?",
          "Mention exclusions to prevent confusion.",
          false,
          optionalCreator({
            defaultValue: "Anything outside the agreed request or requiring substantially additional work may need a separate order.",
            maxLength: 2000
          })
        )
      ],

      buyerFields: [
        buyerField(
          "requirements",
          "Tell the creator what you need",
          "textarea",
          true,
          [],
          {
            maxLength: 3000
          }
        ),

        buyerField(
          "reference_url",
          "Reference URL",
          "url",
          false
        ),

        buyerField(
          "reference_file",
          "Reference file",
          "file",
          false
        ),

        buyerField(
          "preferred_deadline",
          "Preferred deadline",
          "date",
          false
        )
      ],

      orderWorkflow: {
        type: "custom_delivery",
        deliveryMode: "manual",
        supportsText: true,
        supportsFile: true,
        supportsLink: true,
        requiresSchedule: false,
        requiresApproval: false,
        completionFields: [
          "deliveryText",
          "deliveryFile",
          "deliveryLink"
        ]
      },

      validation: {
        minPrice: 99,
        maxPrice: 500000,
        maxBuyerFields: 10
      }
    },

    /* ======================================================================
       6. SOCIAL PROMOTION
       ====================================================================== */
    {
      type: "social_promotion",

      meta: {
        icon: "📱",
        label: "Social Promotion",
        category: "Promotion",
        description:
          "Sell stories, posts, reels, mentions, reviews or campaign bundles.",
        status: "active",
        sortOrder: 60,
        schemaVersion: 1,
        allowMultipleProducts: true,
        defaultCoverImage: defaultCoverImage("social_promotion"),
        defaultCoverAlt: "Social Promotion"
      },

      defaults: {
        title: "Social Media Promotion",
        shortDescription:
          "Book a creator promotion, mention, story, post, reel or campaign.",
        description:
          "Book a social media promotion subject to creator approval. The final content, schedule and format will follow the selected offer and the campaign information provided by the buyer.",
        customerInstructions:
          "Share your brand, campaign goal, required talking points, links and any important restrictions. Promotions remain subject to creator approval and applicable platform disclosure rules.",
        price: 1499,
        currency: "INR",
        priceModel: "fixed",
        badge: "none",
        terms: defaultTerms({
          cancellation:
            "Campaign cancellation after content production has started may not be refundable.",
          revision:
            "Reasonable factual corrections may be requested before publishing when approval is included.",
          usage:
            "Brand reuse, whitelisting, paid advertising and extended usage rights are not included unless explicitly stated in the offer."
        }),
        status: "draft",
        visibility: "private",
        defaultCoverImage: defaultCoverImage("social_promotion")
      },

      presets: [
        {
          id: "instagram_story",
          label: "Instagram Story",
          icon: "📲",
          defaults: {
            title: "Instagram Story Promotion",
            price: 1499
          }
        },
        {
          id: "instagram_reel",
          label: "Instagram Reel",
          icon: "🎬",
          defaults: {
            title: "Instagram Reel Promotion",
            price: 4999
          }
        },
        {
          id: "instagram_post",
          label: "Instagram Post",
          icon: "🖼️",
          defaults: {
            title: "Instagram Feed Post",
            price: 3999
          }
        },
        {
          id: "youtube_short",
          label: "YouTube Short",
          icon: "▶️",
          defaults: {
            title: "YouTube Short Promotion",
            price: 5999
          }
        },
        {
          id: "youtube_mention",
          label: "YouTube Mention",
          icon: "📺",
          defaults: {
            title: "YouTube Video Mention",
            price: 9999
          }
        },
        {
          id: "custom_campaign",
          label: "Custom Campaign",
          icon: "🚀",
          defaults: {
            title: "Custom Social Campaign",
            price: 9999
          }
        }
      ],

      fulfilmentFields: [
        selectField(
          "promotion_platform",
          "Promotion platform",
          [
            { label: "Instagram", value: "instagram" },
            { label: "YouTube", value: "youtube" },
            { label: "Facebook", value: "facebook" },
            { label: "LinkedIn", value: "linkedin" },
            { label: "X / Twitter", value: "x" },
            { label: "Multiple platforms", value: "multiple" },
            { label: "Other", value: "other" }
          ],
          "instagram",
          false,
          optionalCreator()
        ),

        textField(
          "other_platform",
          "Other platform",
          "Enter platform name",
          false,
          optionalCreator({
            visibleWhen: {
              field: "promotion_platform",
              equals: "other"
            },
            maxLength: 100
          })
        ),

        selectField(
          "promotion_format",
          "Promotion format",
          [
            { label: "Story", value: "story" },
            { label: "Feed post", value: "post" },
            { label: "Reel or short video", value: "reel" },
            { label: "Video mention", value: "video_mention" },
            { label: "Product review", value: "review" },
            { label: "Brand shout-out", value: "shoutout" },
            { label: "Link placement", value: "link_placement" },
            { label: "Campaign bundle", value: "bundle" },
            { label: "Custom promotion", value: "custom" }
          ],
          "story",
          false,
          optionalCreator()
        ),

        numberField(
          "quantity",
          "Number of deliverables",
          1,
          false,
          optionalCreator({
            min: 1,
            max: 100
          })
        ),

        numberField(
          "content_retention_hours",
          "Content retention in hours",
          24,
          false,
          optionalCreator({
            min: 1,
            max: 8760,
            helpText:
              "Default is 24 hours. Change this for permanent or longer placements."
          })
        ),

        numberField(
          "minimum_notice_hours",
          "Minimum campaign notice",
          72,
          false,
          optionalCreator({
            min: 1,
            max: 2160
          })
        ),

        numberField(
          "daily_limit",
          "Maximum promotions per day",
          2,
          false,
          optionalCreator({
            min: 1,
            max: 50
          })
        ),

        toggleField(
          "link_allowed",
          "Link placement included",
          true,
          "Enable when a link sticker, caption link or profile link is included."
        ),

        toggleField(
          "approval_required",
          "Creator approval required",
          true,
          "The creator may accept or reject the campaign before fulfilment."
        ),

        toggleField(
          "brand_approval_included",
          "Brand content approval included",
          false,
          "Enable when the buyer may approve content before publishing."
        ),

        toggleField(
          "product_required",
          "Brand must provide product",
          false,
          "Enable for physical-product reviews or demonstrations."
        ),

        textField(
          "accepted_categories",
          "Accepted brand categories",
          "Example: fitness, beauty, lifestyle",
          false,
          optionalCreator({
            defaultValue: "Lifestyle, fashion, beauty, home, food, travel, technology and other suitable brands.",
            maxLength: 300
          })
        ),

        textareaField(
          "usage_rights",
          "Usage rights",
          "Explain whether the brand may reuse the content.",
          false,
          optionalCreator({
            defaultValue: "Organic reposting with creator credit is allowed. Paid advertising, whitelisting or extended usage requires separate approval.",
            maxLength: 1500
          })
        ),

        textareaField(
          "promotion_terms",
          "Promotion terms",
          "Mention restrictions, prohibited categories and disclosure rules.",
          false,
          recommendedCreator({
            defaultValue: "Campaigns must be suitable for my audience and comply with platform disclosure rules. I may decline products, claims or categories that are not a good fit.",
            maxLength: 2500
          })
        )
      ],

      buyerFields: [
        buyerField(
          "brand_name",
          "Brand or business name",
          "text",
          true,
          [],
          {
            maxLength: 150
          }
        ),

        buyerField(
          "brand_url",
          "Brand website or profile",
          "url",
          true
        ),

        buyerField(
          "campaign_goal",
          "Campaign goal",
          "textarea",
          true,
          [],
          {
            maxLength: 1500
          }
        ),

        buyerField(
          "promotion_details",
          "Campaign brief",
          "textarea",
          true,
          [],
          {
            maxLength: 3000
          }
        ),

        buyerField(
          "preferred_date",
          "Preferred publishing date",
          "date",
          false
        ),

        buyerField(
          "campaign_assets_url",
          "Campaign assets link",
          "url",
          false
        )
      ],

      orderWorkflow: {
        type: "social_campaign",
        deliveryMode: "approval_and_proof",
        supportsText: true,
        supportsFile: true,
        supportsLink: true,
        requiresSchedule: true,
        requiresApproval: true,
        completionFields: [
          "acceptedAt",
          "scheduledAt",
          "proofLink",
          "proofFile",
          "completionNote"
        ]
      },

      validation: {
        minPrice: 199,
        maxPrice: 1000000,
        maxBuyerFields: 12
      },

      hooks: {
        validateFulfilment: function (values) {
          var errors = [];

          if (
            values.promotion_platform === "other" &&
            !String(values.other_platform || "").trim()
          ) {
            errors.push(
              "Other platform is required when Other is selected."
            );
          }

          return errors;
        }
      }
    }
  ];

  function validateWorkflowDefinitions(definitions) {
    var seen = Object.create(null);

    definitions.forEach(function (workflow, index) {
      var type = Engine.normalizeType(workflow && workflow.type);

      if (!type) {
        throw new Error(
          "Workflow " + (index + 1) + " requires a valid type."
        );
      }

      if (seen[type]) {
        throw new Error(
          'Duplicate workflow type "' + type + '".'
        );
      }
      seen[type] = true;

      if (!workflow.meta || !String(workflow.meta.label || "").trim()) {
        throw new Error(
          'Workflow "' + type + '" requires a display label.'
        );
      }

      var coverImage = String(
        (workflow.meta && workflow.meta.defaultCoverImage) ||
        (workflow.defaults && workflow.defaults.defaultCoverImage) ||
        ""
      ).trim();

      if (!/^https:\/\//i.test(coverImage)) {
        throw new Error(
          'Workflow "' + type + '" requires a valid HTTPS default cover image.'
        );
      }

      if (!workflow.defaults) {
        throw new Error(
          'Workflow "' + type + '" requires defaults.'
        );
      }

      var price = Number(workflow.defaults.price);
      var validation = workflow.validation || {};
      var minPrice = validation.minPrice == null
        ? null
        : Number(validation.minPrice);
      var maxPrice = validation.maxPrice == null
        ? null
        : Number(validation.maxPrice);

      if (!Number.isFinite(price) || price < 0) {
        throw new Error(
          'Workflow "' + type + '" has an invalid default price.'
        );
      }

      if (Number.isFinite(minPrice) && price < minPrice) {
        throw new Error(
          'Workflow "' + type + '" default price is below its minimum price.'
        );
      }

      if (Number.isFinite(maxPrice) && price > maxPrice) {
        throw new Error(
          'Workflow "' + type + '" default price exceeds its maximum price.'
        );
      }

      var completionFields =
        workflow.orderWorkflow &&
        Array.isArray(workflow.orderWorkflow.completionFields)
          ? workflow.orderWorkflow.completionFields
          : [];

      completionFields.forEach(function (field) {
        if (!String(field || "").trim()) {
          throw new Error(
            'Workflow "' + type + '" contains an empty completion field.'
          );
        }
      });
    });

    return true;
  }

  validateWorkflowDefinitions(workflows);
  Engine.registerWorkflows(workflows);

  window.ClikkerProductWorkflows = Object.freeze({
    loaded: true,
    version: VERSION,
    requiredEngineVersion: "1.2.2",
    engineVersion: Engine.version || "",
    creatorFriendlyDefaults: true,
    minimalPublishBlockers: true,
    defaultCoverImages: DEFAULT_COVER_IMAGES,
    getDefaultCoverImage: function (type) {
      return defaultCoverImage(type);
    },
    count: workflows.length,
    types: Object.freeze(
      workflows.map(function (workflow) {
        return workflow.type;
      })
    )
  });

  console.info(
    "[Clikker Product Workflows] Registered",
    workflows.length,
    "workflows",
    VERSION,
    "with Engine",
    Engine.version || "unknown",
    "and",
    Object.keys(DEFAULT_COVER_IMAGES).length,
    "default product covers"
  );
})(window);
